import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  alpha,
  useTheme,
  InputAdornment,
} from '@mui/material';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import AnimateButton from '@/components/ui/AnimateButton';
import { phoneSchema, type PhoneValues } from './zod';

interface PhoneStepProps {
  phone: string;
  onChange: (val: string) => void;
  onSubmit: () => void;
  error: string;
  loading: boolean;
  verifyFailed?: boolean;
  submitLabel?: string;
}

export default function PhoneStep({
  phone,
  onChange,
  onSubmit,
  error,
  loading,
  verifyFailed,
  submitLabel = 'Send OTP',
}: PhoneStepProps) {
  const theme = useTheme();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PhoneValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone },
    mode: 'onBlur',
  });

  // Keep form in sync when parent clears/resets phone (e.g. back navigation)
  useEffect(() => {
    setValue('phone', phone, { shouldValidate: phone.length > 0 });
  }, [phone, setValue]);

  const validationError = errors.phone?.message;
  const displayError = validationError || error || undefined;

  return (
    <Box component="form" onSubmit={handleSubmit(() => onSubmit())} noValidate>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <PhoneAndroidOutlinedIcon sx={{ color: 'primary.main', fontSize: 28 }} />
      </Box>

      <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
        Sign in
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Enter your registered phone number.
      </Typography>

      <Stack spacing={2.5}>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="phone-input"
              label="Phone Number"
              type="tel"
              fullWidth
              autoFocus
              autoComplete="tel"
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                field.onChange(digitsOnly);
                onChange(digitsOnly);
              }}
              error={!!displayError}
              helperText={displayError}
              inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      +91
                    </Typography>
                  </InputAdornment>
                ),
              }}
              placeholder="10-digit mobile number"
            />
          )}
        />

        <AnimateButton>
          <Button
            id="send-otp-btn"
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !!verifyFailed}
            sx={{ py: 1.5, fontSize: '0.9375rem', borderRadius: '10px' }}
          >
            {loading ? '…' : submitLabel}
          </Button>
        </AnimateButton>
      </Stack>
    </Box>
  );
}
